import type { AuditLogListParams, AuditLogListResponse, AuditLogResponse } from '../types';
import type { AdminAuditLogExportRecord } from '../types';
import type {
  BinaryExportOptions,
  ExportEnvelope,
  ExportOptions,
  JsonExportOptions,
} from '../../types';
import type { RequestFn } from '../../client';
import { runExport } from '../../exportDownload';

export class AuditLogs {
  constructor(private readonly request: RequestFn) {}

  async list(params?: AuditLogListParams): Promise<AuditLogListResponse> {
    return this.request('GET', '/admin-portal/audit', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  async retrieve(logId: string): Promise<AuditLogResponse> {
    return this.request('GET', `/admin-portal/audit/${encodeURIComponent(logId)}`);
  }

  /**
   * The platform audit log as a file. `GET /admin-portal/audit/export`
   *
   * Takes the same filters as {@link AuditLogs.list}, and returns the whole
   * match set rather than a page — bounded at 25,000 rows (500 for `pdf`), and
   * refused past that with `DE_07` rather than truncated. `csv` and `json` are
   * streamed and held to their `X-Delopay-Record-Count`; see
   * {@link ExportTransferOptions} for progress, cancellation and the
   * `EXPORT_INCOMPLETE` check.
   *
   * Columns answer the question an audit asks rather than mirroring the
   * dashboard table: the actor, the real actor behind an impersonation, the
   * action, its target, when, and from where. `details` is deliberately absent —
   * an unbounded per-action blob is unreadable in a spreadsheet cell.
   */
  async export(
    params?: AuditLogListParams,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<AdminAuditLogExportRecord>>;
  async export(params: AuditLogListParams | undefined, options: BinaryExportOptions): Promise<Blob>;
  async export(
    params: AuditLogListParams | undefined,
    options: ExportOptions,
  ): Promise<ExportEnvelope<AdminAuditLogExportRecord> | Blob>;
  async export(
    params?: AuditLogListParams,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<AdminAuditLogExportRecord> | Blob> {
    return runExport(
      this.request,
      'GET',
      '/admin-portal/audit/export',
      // The format is negotiated by `Accept`, never a query field: the query
      // here is the list's own filter model, and adding a key to it would fork
      // the filter vocabulary the export exists to reuse.
      { query: params as Record<string, string | number | boolean | undefined> },
      options,
    );
  }
}
